/**
 * RoyalEngagement — North Indian royal engagement (Rajput/Punjabi style).
 *
 * A fully data-driven, single-page experience built from the resolved
 * `RoyalContent` object. Self-registers with the shared template `registry`
 * on import so PublicInvitation.tsx can route here via registry.has(templateId).
 *
 * Section order:
 *   Hero → Families → Ceremony → Programme → RSVP
 *
 * Registry ID `royal-engagement-experience` MUST match the backend Template row
 * (introduced via Prisma migration) and StepTemplate.tsx.
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { royalTheme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { FamiliesSection } from './sections/FamiliesSection';
import { CeremonySection } from './sections/CeremonySection';
import { ProgrammeSection } from './sections/ProgrammeSection';
import { RsvpSection } from './sections/RsvpSection';

const META: TemplateMetadata = {
  id: 'royal-engagement-experience',
  name: 'Royal Engagement — Rajput Royal',
  occasionType: 'ENGAGEMENT',
  description:
    'A regal North Indian engagement invitation: animated rose-petal hero, ring-box reveal, both families, ring ceremony and a royal celebration programme with RSVP.',
  isPremium: true,
  tags: ['engagement', 'royal', 'rajput', 'punjabi', 'animated', 'premium'],
  colorScheme: {
    primary: royalTheme.maroon,
    accent: royalTheme.gold,
    bg: royalTheme.ivory,
  },
};

export function RoyalEngagementTemplate({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  const scrollToRsvp = React.useCallback(() => {
    document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main
      className="royal-root min-h-screen scroll-smooth bg-[var(--royal-ivory)] font-sans text-[var(--royal-ink)] antialiased"
      style={themeCssVars(royalTheme)}
    >
      <HeroSection content={content.hero} onRsvp={scrollToRsvp} />
      <FamiliesSection content={content.families} />
      <CeremonySection content={content.ceremony} />
      <ProgrammeSection content={content.programme} />
      <RsvpSection
        content={content.rsvp}
        invitationId={invitationId}
        isLive={isLive}
      />
    </main>
  );
}

registry.register({ metadata: META, component: RoyalEngagementTemplate });
